// Persisted callouts remain regular imageHotspots. Their coords describe the
// complete outer bounds; the pointer is carved out of those bounds so moving,
// resizing, rotating, and timing continue to use the legacy overlay model.
export const CALLOUT_POINTER_POSITIONS = [
	"top-left",
	"top-middle",
	"top-right",
	"right-middle",
	"bottom-right",
	"bottom-middle",
	"bottom-left",
	"left-middle",
	"none"
];
export const CALLOUT_POINTER_POSITION_LABEL = "Pointer Position";

// These single-family suggestions are shared by the activity/web/mobile
// rendering model. The authoring control remains free text so activity-defined
// and customer-provided family names are preserved without being allowlisted.
export const CALLOUT_FONT_FAMILY_OPTIONS = [
	"Arial",
	"Helvetica",
	"Roboto",
	"Roboto Condensed",
	"Open Sans Condensed",
	"Rubik",
	"Courier",
	"Georgia"
];

export const CALLOUT_DEFAULTS = {
	text: "Callout text",
	pointerPosition: "bottom-left",
	style: {
		borderColor: "rgba(15,23,42,1)",
		borderWidth: 2,
		borderRadius: 12,
		borderFillColor: "rgba(15,23,42,1)",
		textColor: "rgba(255,255,255,1)",
		fontFamily: "",
		fontSize: 32,
		fontWeight: "600",
		textAlign: "center",
		verticalAlign: "middle",
		padding: 16,
		lineHeight: 1.2,
		pointerSize: 24
	}
};

function toFiniteNumber(value, fallback = 0) {
	if (value === undefined || value === null || value === "") {
		return fallback;
	}
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? numericValue : fallback;
}

function clampValue(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function normalizeOption(value, allowedValues, fallback) {
	const normalizedValue = `${value || ""}`.trim().toLowerCase();
	return allowedValues.includes(normalizedValue) ? normalizedValue : fallback;
}

export function normalizeCalloutPointerPosition(value = CALLOUT_DEFAULTS.pointerPosition) {
	return normalizeOption(value, CALLOUT_POINTER_POSITIONS, CALLOUT_DEFAULTS.pointerPosition);
}

export function normalizeCalloutTextAlign(value = CALLOUT_DEFAULTS.style.textAlign) {
	return normalizeOption(value, ["left", "center", "right"], CALLOUT_DEFAULTS.style.textAlign);
}

export function normalizeCalloutVerticalAlign(value = CALLOUT_DEFAULTS.style.verticalAlign) {
	return normalizeOption(value, ["top", "middle", "bottom"], CALLOUT_DEFAULTS.style.verticalAlign);
}

export function normalizeCalloutFontWeight(value = CALLOUT_DEFAULTS.style.fontWeight) {
	const normalizedValue = `${value === undefined || value === null ? "" : value}`.trim().toLowerCase();
	return ["normal", "bold", "300", "400", "500", "600", "700", "800", "900"].includes(normalizedValue)
		? normalizedValue
		: CALLOUT_DEFAULTS.style.fontWeight;
}

export function normalizeCalloutFontFamily(value = CALLOUT_DEFAULTS.style.fontFamily) {
	return typeof value === "string" && value.trim() ? value : CALLOUT_DEFAULTS.style.fontFamily;
}

export function getCalloutPresentation(item = {}) {
	const style = item.style && typeof item.style === "object" ? item.style : {};
	const authoredText = typeof item.text === "string" ? item.text : "";
	const textColor = typeof style.textColor === "string" && style.textColor.trim()
		? style.textColor
		: CALLOUT_DEFAULTS.style.textColor;

	return {
		text: authoredText,
		pointerPosition: normalizeCalloutPointerPosition(item.pointerPosition),
		textColor,
		fontFamily: normalizeCalloutFontFamily(style.fontFamily),
		fontSize: clampValue(toFiniteNumber(style.fontSize, CALLOUT_DEFAULTS.style.fontSize), 1, 512),
		fontWeight: normalizeCalloutFontWeight(style.fontWeight),
		textAlign: normalizeCalloutTextAlign(style.textAlign),
		verticalAlign: normalizeCalloutVerticalAlign(style.verticalAlign),
		padding: clampValue(toFiniteNumber(style.padding, CALLOUT_DEFAULTS.style.padding), 0, 4096),
		lineHeight: clampValue(toFiniteNumber(style.lineHeight, CALLOUT_DEFAULTS.style.lineHeight), 0.5, 512),
		pointerSize: clampValue(toFiniteNumber(style.pointerSize, CALLOUT_DEFAULTS.style.pointerSize), 0, 4096)
	};
}

export function getCalloutGeometry(coords = {}, pointerPosition = CALLOUT_DEFAULTS.pointerPosition, pointerSize = CALLOUT_DEFAULTS.style.pointerSize, borderRadius = CALLOUT_DEFAULTS.style.borderRadius) {
	const x = toFiniteNumber(coords.x, 0);
	const y = toFiniteNumber(coords.y, 0);
	const width = Math.max(toFiniteNumber(coords.width, 0), 0);
	const height = Math.max(toFiniteNumber(coords.height, 0), 0);
	const normalizedPointerPosition = normalizeCalloutPointerPosition(pointerPosition);
	const pointerVisible = normalizedPointerPosition !== "none";
	const maxPointerSize = Math.max(Math.min(height * 0.45, width * 0.45), 0);
	const safePointerSize = pointerVisible
		? clampValue(toFiniteNumber(pointerSize, CALLOUT_DEFAULTS.style.pointerSize), 0, maxPointerSize)
		: 0;
	const pointerOnTop = pointerVisible && normalizedPointerPosition.indexOf("top-") === 0;
	const pointerOnBottom = pointerVisible && normalizedPointerPosition.indexOf("bottom-") === 0;
	const pointerOnLeftEdge = normalizedPointerPosition === "left-middle";
	const pointerOnRightEdge = normalizedPointerPosition === "right-middle";
	const pointerAtLeftCorner = pointerVisible && normalizedPointerPosition.indexOf("-left") > -1;
	const bubbleLeft = pointerOnLeftEdge ? x + safePointerSize : x;
	const bubbleRight = pointerOnRightEdge ? x + width - safePointerSize : x + width;
	const bubbleTop = pointerOnTop ? y + safePointerSize : y;
	const bubbleBottom = pointerOnBottom ? y + height - safePointerSize : y + height;
	const bubbleWidth = Math.max(bubbleRight - bubbleLeft, 0);
	const bubbleHeight = Math.max(bubbleBottom - bubbleTop, 0);
	const radius = clampValue(toFiniteNumber(borderRadius, 0), 0, Math.min(bubbleWidth / 2, bubbleHeight / 2));
	const horizontalBaseLength = Math.min(Math.max(safePointerSize * 1.6, 0), Math.max(bubbleWidth - (radius * 2), 0));
	const verticalBaseLength = Math.min(Math.max(safePointerSize * 1.6, 0), Math.max(bubbleHeight - (radius * 2), 0));
	const pointerNearX = pointerAtLeftCorner ? bubbleLeft + radius : bubbleRight - radius;
	const pointerFarX = pointerAtLeftCorner
		? Math.min(bubbleRight - radius, pointerNearX + horizontalBaseLength)
		: Math.max(bubbleLeft + radius, pointerNearX - horizontalBaseLength);
	const horizontalBaseStart = bubbleLeft + ((bubbleWidth - horizontalBaseLength) / 2);
	const horizontalBaseEnd = horizontalBaseStart + horizontalBaseLength;
	const verticalBaseStart = bubbleTop + ((bubbleHeight - verticalBaseLength) / 2);
	const verticalBaseEnd = verticalBaseStart + verticalBaseLength;
	let pointerTip = null;
	if (pointerOnTop || pointerOnBottom) {
		pointerTip = {
			x: normalizedPointerPosition.indexOf("-middle") > -1
				? x + (width / 2)
				: (pointerAtLeftCorner ? x : x + width),
			y: pointerOnTop ? y : y + height
		};
	} else if (pointerOnLeftEdge || pointerOnRightEdge) {
		pointerTip = {
			x: pointerOnLeftEdge ? x : x + width,
			y: y + (height / 2)
		};
	}
	const topTailPath = pointerVisible && pointerOnTop
		? (normalizedPointerPosition === "top-middle"
			? `L ${horizontalBaseStart} ${bubbleTop} L ${pointerTip.x} ${pointerTip.y} L ${horizontalBaseEnd} ${bubbleTop}`
			: (pointerAtLeftCorner
				? `L ${pointerNearX} ${bubbleTop} L ${pointerTip.x} ${pointerTip.y} L ${pointerFarX} ${bubbleTop}`
				: `L ${pointerFarX} ${bubbleTop} L ${pointerTip.x} ${pointerTip.y} L ${pointerNearX} ${bubbleTop}`))
		: "";
	const rightTailPath = pointerOnRightEdge
		? `L ${bubbleRight} ${verticalBaseStart} L ${pointerTip.x} ${pointerTip.y} L ${bubbleRight} ${verticalBaseEnd}`
		: "";
	const bottomTailPath = pointerVisible && pointerOnBottom
		? (normalizedPointerPosition === "bottom-middle"
			? `L ${horizontalBaseEnd} ${bubbleBottom} L ${pointerTip.x} ${pointerTip.y} L ${horizontalBaseStart} ${bubbleBottom}`
			: (pointerAtLeftCorner
				? `L ${pointerFarX} ${bubbleBottom} L ${pointerTip.x} ${pointerTip.y} L ${pointerNearX} ${bubbleBottom}`
				: `L ${pointerNearX} ${bubbleBottom} L ${pointerTip.x} ${pointerTip.y} L ${pointerFarX} ${bubbleBottom}`))
		: "";
	const leftTailPath = pointerOnLeftEdge
		? `L ${bubbleLeft} ${verticalBaseEnd} L ${pointerTip.x} ${pointerTip.y} L ${bubbleLeft} ${verticalBaseStart}`
		: "";
	const path = [
		`M ${bubbleLeft + radius} ${bubbleTop}`,
		topTailPath,
		`L ${bubbleRight - radius} ${bubbleTop}`,
		`Q ${bubbleRight} ${bubbleTop} ${bubbleRight} ${bubbleTop + radius}`,
		rightTailPath,
		`L ${bubbleRight} ${bubbleBottom - radius}`,
		`Q ${bubbleRight} ${bubbleBottom} ${bubbleRight - radius} ${bubbleBottom}`,
		bottomTailPath,
		`L ${bubbleLeft + radius} ${bubbleBottom}`,
		`Q ${bubbleLeft} ${bubbleBottom} ${bubbleLeft} ${bubbleBottom - radius}`,
		leftTailPath,
		`L ${bubbleLeft} ${bubbleTop + radius}`,
		`Q ${bubbleLeft} ${bubbleTop} ${bubbleLeft + radius} ${bubbleTop}`,
		"Z"
	].filter(Boolean).join(" ");

	return {
		outerRect: { x, y, width, height },
		bubbleRect: {
			x: bubbleLeft,
			y: bubbleTop,
			width: bubbleWidth,
			height: bubbleHeight
		},
		pointerPosition: normalizedPointerPosition,
		pointerSize: safePointerSize,
		pointerTip,
		borderRadius: radius,
		path
	};
}

export function getCalloutTextRect(item = {}) {
	const presentation = getCalloutPresentation(item);
	const geometry = getCalloutGeometry(
		item.coords || {},
		presentation.pointerPosition,
		presentation.pointerSize,
		item.style && item.style.borderRadius
	);
	const maxPadding = Math.max(Math.min(geometry.bubbleRect.width / 2, geometry.bubbleRect.height / 2), 0);
	const padding = clampValue(presentation.padding, 0, maxPadding);

	return {
		x: geometry.bubbleRect.x + padding,
		y: geometry.bubbleRect.y + padding,
		width: Math.max(geometry.bubbleRect.width - (padding * 2), 0),
		height: Math.max(geometry.bubbleRect.height - (padding * 2), 0),
		padding
	};
}
